import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  type TechChartRow,
  type MonthChartRow,
  truncateWithEllipsis,
} from '../../../utils/orgBillingAnalysisUtils';

interface TooltipPayloadItem {
  name: string;
  value: number;
  color?: string;
}

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  formatVal: (n: number) => string;
}> = ({ active, payload, label, formatVal }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[12px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full flex-none"
            style={{ background: p.color ?? '#64748b' }}
          />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-semibold text-slate-800">
            {formatVal(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 min-h-[300px] flex flex-col">
    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
      {title}
    </p>
    <div className="flex-1 min-h-[240px]">{children}</div>
  </div>
);

const BLUE = '#2563EB';
const SLATE = '#94a3b8';

export interface TechnologyBillingChartsSectionProps {
  /** First bar chart (technology, grade, or project breakdown). */
  primaryChartRows: TechChartRow[];
  primaryChartTitle: string;
  chartByMonth: MonthChartRow[];
  topPrimaryLimit?: number;
  formatHours: (n: number) => string;
  /** Project tab: shorten Y-axis labels (tooltip still uses full `name`). */
  primaryYTickTruncateAt?: number;
}

export function TechnologyBillingChartsSection({
  primaryChartRows,
  primaryChartTitle,
  chartByMonth,
  topPrimaryLimit = 12,
  formatHours,
  primaryYTickTruncateAt,
}: TechnologyBillingChartsSectionProps) {
  const primaryData = primaryChartRows.slice(0, topPrimaryLimit);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <ChartCard title={primaryChartTitle}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={primaryData}
            layout="vertical"
            margin={{ left: 4, right: 12, top: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" tick={{ fontSize: 10 }} stroke="#64748b" />
            <YAxis
              type="category"
              dataKey="name"
              width={primaryYTickTruncateAt ? 120 : 108}
              tick={{ fontSize: 10 }}
              stroke="#64748b"
              tickFormatter={
                primaryYTickTruncateAt
                  ? (v: string) =>
                      truncateWithEllipsis(String(v), primaryYTickTruncateAt)
                  : undefined
              }
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  active={props.active}
                  payload={props.payload as TooltipPayloadItem[]}
                  label={props.label as string}
                  formatVal={formatHours}
                />
              )}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="billable" name="Billable" stackId="a" fill={BLUE} />
            <Bar
              dataKey="nonBillable"
              name="Non-billable"
              stackId="a"
              fill={SLATE}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Hours over time (by month)">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartByMonth}
            margin={{ left: 4, right: 12, top: 8, bottom: 24 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9 }}
              stroke="#64748b"
              interval="preserveStartEnd"
              angle={chartByMonth.length > 12 ? -35 : 0}
              textAnchor={chartByMonth.length > 12 ? 'end' : 'middle'}
              height={chartByMonth.length > 12 ? 56 : 32}
            />
            <YAxis tick={{ fontSize: 10 }} stroke="#64748b" />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  active={props.active}
                  payload={props.payload as TooltipPayloadItem[]}
                  label={props.label as string}
                  formatVal={formatHours}
                />
              )}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="billable" name="Billable" fill={BLUE} />
            <Bar dataKey="nonBillable" name="Non-billable" fill={SLATE} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
